import {
  useState,
  useEffect,
  useRef,
  useContext,
  type ChangeEvent,
  type MouseEvent,
  type ComponentProps,
} from "react";
import {
  Box,
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Dialog,
  IconButton,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getUsers,
  getOrganizations,
  downloadSiteByUsers,
  parseSiteByUsersDownloadResponse,
} from "../../../api";
import {
  Close as CloseIcon,
  ModeEditOutline as ModeEditOutlineIcon,
} from "@mui/icons-material";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../components/SnackBarCustom";
import RoleSelect from "./RoleSelect";
import OrganizationSelect, {
  type OrganizationOption,
} from "./OrganizationSelect";
import { UserRole, UserMasterRole } from "../../../role";
import { TokenContext, type TokenContextValue } from "../../../TokenContext";
import type { IdParam } from "../../../types/common";
import type { GetUsersParams, UserListItem, UserListOrganization } from "../../../types/user";

const ORG_LIMIT = 1000;
const SITEBYUSERS_DOMAIN = "cosmo-oil.co.jp";
const SITEBYUSERS_DISASTER_ID = 0;

type AccountListColumn = {
  id: keyof UserListItem | "operation" | "role";
  label: string;
  minWidth: string;
  align?: "left" | "right";
  isSmallScreen: boolean;
  organizations?: boolean;
  role?: boolean;
  button?: boolean;
};

const columns: AccountListColumn[] = [
  {
    id: "open_id",
    label: "ログインID",
    minWidth: "fit-content",
    isSmallScreen: true,
  },
  {
    id: "m_organizations",
    label: "所属",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
    organizations: true,
  },
  {
    id: "role",
    label: "権限",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
    role: true,
  },
  {
    id: "name",
    label: "名前",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: true,
  },
  {
    id: "operation",
    label: "",
    minWidth: "fit-content",
    align: "right",
    button: true,
    isSmallScreen: true,
  },
];

const formGridRowSx = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "16px 24px",
  width: "100%",
  alignItems: "center",
  "@media screen and (max-width:900px)": {
    gridTemplateColumns: "1fr",
  },
};

const formFieldSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  width: "100%",
  fontSize: 16,
  fontWeight: "bold",
  color: "#000",
  whiteSpace: "nowrap",
};

const formFieldLabelSx = {
  width: "96px",
  minWidth: "96px",
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const formFieldControlSx = {
  flex: 1,
  minWidth: 0,
};

type SearchFormState = {
  open_id: string;
  name: string;
  m_role_id: number | null;
  division: OrganizationOption | null;
  company: OrganizationOption | null;
  organizations: OrganizationOption[];
  organizationOptions: OrganizationOption[];
};

type FetchUsersOptions = {
  showSnackbar?: boolean;
};

type ButtonValidateProps = {
  userId: IdParam;
  userRole: ReturnType<typeof UserRole>;
  onNavigate: (event: MouseEvent, path: string) => void;
};

const ButtonValidate = ({
  userId,
  userRole,
  onNavigate,
}: ButtonValidateProps) => {
  return userRole.update ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        height: "36px",
        width: "100%",
      }}
    >
      <IconButton
        name="editButton"
        color="primary"
        onClick={(event) =>
          onNavigate(event, `/account/list/edit?user-id=${userId}`)
        }
        sx={{ pointerEvents: "auto" }}
      >
        <ModeEditOutlineIcon />
      </IconButton>
    </Box>
  ) : (
    ""
  );
};

const formatOrganizationAffiliation = (
  organization: UserListOrganization
): string => {
  const grandParent =
    organization.parent_m_organization?.parent_m_organization
      ?.department_name || "";
  const parent = organization.parent_m_organization?.department_name || "";
  const self = organization.department_name || "";
  return [grandParent, parent, self].filter(Boolean).join(" ");
};

const OrganizationsDisplay = ({
  organizations,
}: {
  organizations?: UserListOrganization[];
}) => {
  if (!organizations?.length) {
    return null;
  }
  return (
    <Box>
      {organizations.map((organization, index) => (
        <Box key={(organization.id as IdParam | undefined) ?? index}>
          {formatOrganizationAffiliation(organization)}
        </Box>
      ))}
    </Box>
  );
};

const RolesDisplay = ({
  organizations,
}: {
  organizations?: UserListOrganization[];
}) => {
  if (!organizations?.length) {
    return null;
  }
  return (
    <Box>
      {organizations.map((organization, index) => (
        <Box key={(organization.id as IdParam | undefined) ?? index}>
          {organization.m_role?.name || ""}
        </Box>
      ))}
    </Box>
  );
};

const getScalarCellValue = (
  user: UserListItem,
  column: AccountListColumn
): string => {
  if (column.id === "operation" || column.id === "role") {
    return "";
  }
  const value = user[column.id];
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return value != null ? String(value) : "";
};

const emptySearchForm = (): SearchFormState => ({
  open_id: "",
  name: "",
  m_role_id: null,
  division: null,
  company: null,
  organizations: [],
  organizationOptions: [],
});

const cloneSearchForm = (form: SearchFormState): SearchFormState => ({
  ...form,
  organizations: [...(form.organizations || [])],
  organizationOptions: [...(form.organizationOptions || [])],
});

export default function AccountList() {
  const { user } = useContext(TokenContext) as TokenContextValue;
  const userRole = UserRole(user!.m_organization.m_role_id);
  const userMasterRole = UserMasterRole(user!.m_organization.m_role_id);

  const navigate = useNavigate();
  const handleClick = (event: MouseEvent, path: string) => {
    event.stopPropagation();
    navigate(path);
  };

  const [selectedRow, setSelectedRow] = useState<UserListItem | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (row: UserListItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalData, setTotalData] = useState(0);

  const [searchForm, setSearchForm] = useState<SearchFormState>(emptySearchForm);
  const [divisionOptions, setDivisionOptions] = useState<OrganizationOption[]>(
    []
  );
  const [companyOptions, setCompanyOptions] = useState<OrganizationOption[]>(
    []
  );

  const lastSearchedContentRef = useRef<SearchFormState | null>(null);

  const buildOrganizationIds = (form: SearchFormState): number[] | null => {
    const selected = form.organizations || [];
    if (!form.company || selected.length === 0) {
      return null;
    }
    return selected.map((item) => item.id);
  };

  const buildUsersParams = (
    form: SearchFormState,
    pageToUse: number,
    limitToUse: number
  ): GetUsersParams => {
    const params: GetUsersParams = {
      open_id: form.open_id || null,
      name: form.name || null,
      m_branch_id: null,
      m_organization_id: buildOrganizationIds(form),
      order:
        "m_organizations.parent_m_organization_id, m_organizations.code, m_organizations.id",
      sort: null,
      page: pageToUse + 1,
      limit: limitToUse,
      m_role_id: form.m_role_id != null ? [form.m_role_id] : null,
    };
    return params;
  };

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchUsers = async (
    overridePage?: number,
    options: FetchUsersOptions = {}
  ) => {
    const { showSnackbar = false } = options;
    const pageToUse = overridePage !== undefined ? overridePage : page;
    const snapshot =
      overridePage !== undefined
        ? cloneSearchForm(searchForm)
        : cloneSearchForm(lastSearchedContentRef.current ?? searchForm);
    try {
      const response = await getUsers(
        buildUsersParams(snapshot, pageToUse, rowsPerPage)
      );
      if (response.status !== "ok") {
        setSnackBarstatus(response);
        setSnackbarOpen(true);
        return;
      }
      setUsers(response.users);
      setTotalData(response.total_count);
      if (overridePage !== undefined) {
        setPage(overridePage);
        lastSearchedContentRef.current = snapshot;
      }
      if (showSnackbar) {
        setSnackBarstatus(response);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
      setSnackBarstatus({
        status: "ng",
        message: "ユーザー情報を取得できませんでした",
      });
      setSnackbarOpen(true);
    }
  };

  const fetchDivisionOptions = async () => {
    try {
      const response = await getOrganizations({
        parent_m_organization_id: 0,
        order: null,
        sort: null,
        page: 1,
        limit: ORG_LIMIT,
      });
      if (response.status === "ok") {
        setDivisionOptions(response.m_organizations || []);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const fetchCompanyOptions = async (divisionId: number) => {
    try {
      const response = await getOrganizations({
        parent_m_organization_id: divisionId,
        order: null,
        sort: null,
        page: 1,
        limit: ORG_LIMIT,
      });
      if (response.status === "ok") {
        setCompanyOptions(response.m_organizations || []);
      } else {
        setCompanyOptions([]);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
      setCompanyOptions([]);
    }
  };

  const fetchOrganizationOptions = async (companyId: number) => {
    try {
      const response = await getOrganizations({
        parent_m_organization_id: companyId,
        order: null,
        sort: null,
        page: 1,
        limit: ORG_LIMIT,
      });
      if (response.status === "ok") {
        const organizations = response.m_organizations || [];
        setSearchForm((prev) => ({
          ...prev,
          organizationOptions: organizations,
          organizations,
        }));
      } else {
        setSearchForm((prev) => ({
          ...prev,
          organizationOptions: [],
          organizations: [],
        }));
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
      setSearchForm((prev) => ({
        ...prev,
        organizationOptions: [],
        organizations: [],
      }));
    }
  };

  useEffect(() => {
    void fetchDivisionOptions();
    void fetchUsers(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const handleChangeRole = (value: { id?: number; name?: string }) => {
    setSearchForm((prev) => ({
      ...prev,
      m_role_id: value?.id ?? null,
    }));
  };

  const handleDivisionChange = (value: OrganizationOption | null) => {
    setSearchForm((prev) => ({
      ...prev,
      division: value,
      company: null,
      organizations: [],
      organizationOptions: [],
    }));
    setCompanyOptions([]);
    if (value?.id) {
      void fetchCompanyOptions(value.id);
    }
  };

  const handleCompanyChange = (value: OrganizationOption | null) => {
    setSearchForm((prev) => ({
      ...prev,
      company: value,
      organizations: [],
      organizationOptions: [],
    }));
    if (value?.id) {
      void fetchOrganizationOptions(value.id);
    }
  };

  const handleOrganizationsChange = (value: OrganizationOption[]) => {
    setSearchForm((prev) => ({
      ...prev,
      organizations: value || [],
    }));
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSiteByUsersDownload = async () => {
    try {
      const response = await downloadSiteByUsers(SITEBYUSERS_DISASTER_ID, {
        domain: SITEBYUSERS_DOMAIN,
      });
      const result = await parseSiteByUsersDownloadResponse(response);
      if (result.status === "ng") {
        setSnackBarstatus({
          status: "ng",
          message: result.message,
        });
        setSnackbarOpen(true);
        return;
      }
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("API呼び出しエラー", error);
      setSnackBarstatus({
        status: "ng",
        message: "ダウンロードに失敗しました",
      });
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: "48px",
          alignItems: "center",
          paddingLeft: 5,
          fontSize: 20,
          fontWeight: "bold",
          whiteSpace: "nowrap",
        }}
      >
        アカウント一覧
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          borderRadius: 2,
          borderColor: "primary.main",
          boxShadow: 3,
          color: "outline.dark",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索条件
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: "16px",
              width: "100%",
            }}
          >
            <Box sx={formGridRowSx}>
              <Box sx={formFieldSx}>
                <Box sx={formFieldLabelSx}>ログインID</Box>
                <Box sx={formFieldControlSx}>
                  <TextField
                    name="openIdInput"
                    size="small"
                    fullWidth
                    value={searchForm.open_id}
                    onChange={(event) =>
                      setSearchForm((prev) => ({
                        ...prev,
                        open_id: event.target.value,
                      }))
                    }
                  />
                </Box>
              </Box>
              <Box sx={formFieldSx}>
                <Box sx={formFieldLabelSx}>名前</Box>
                <Box sx={formFieldControlSx}>
                  <TextField
                    name="nameInput"
                    size="small"
                    fullWidth
                    value={searchForm.name}
                    onChange={(event) =>
                      setSearchForm((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={formGridRowSx}>
              <Box sx={formFieldSx}>
                <Box sx={formFieldLabelSx}>所属区分</Box>
                <Box sx={formFieldControlSx}>
                  <OrganizationSelect
                    name="divisionSelect"
                    label="所属区分選択"
                    options={divisionOptions}
                    value={searchForm.division}
                    onChange={handleDivisionChange}
                  />
                </Box>
              </Box>
              <Box sx={formFieldSx}>
                <Box sx={formFieldLabelSx}>所属会社</Box>
                <Box sx={formFieldControlSx}>
                  <OrganizationSelect
                    name="companySelect"
                    label="所属会社選択"
                    options={companyOptions}
                    value={searchForm.company}
                    onChange={handleCompanyChange}
                    disabled={!searchForm.division}
                  />
                </Box>
              </Box>
              <Box sx={formFieldSx}>
                <Box sx={formFieldLabelSx}>所属組織</Box>
                <Box sx={formFieldControlSx}>
                  <OrganizationSelect
                    name="organizationSelect"
                    label="所属組織選択"
                    multiple
                    options={searchForm.organizationOptions}
                    value={searchForm.organizations}
                    onChange={handleOrganizationsChange}
                    disabled={!searchForm.company}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={formGridRowSx}>
              <Box sx={formFieldSx}>
                <Box sx={formFieldLabelSx}>権限区分</Box>
                <Box sx={formFieldControlSx}>
                  <RoleSelect
                    changedValue={(value) => handleChangeRole(value)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "16px",
                "@media screen and (max-width:600px)": {
                  flexDirection: "column",
                  alignItems: "center",
                },
              }}
            >
              <Button
                name="searchButton"
                variant="contained"
                sx={{ width: "208px" }}
                onClick={() => {
                  setPage(0);
                  void fetchUsers(0, { showSnackbar: true });
                }}
              >
                検索
              </Button>
              <Button
                variant="outlined"
                onClick={(e) => handleClick(e, "/account/list/import")}
                sx={{ width: "208px" }}
              >
                アカウントインポート
              </Button>
              {userMasterRole.read ? (
                <Button
                  name="accountRegistrationButton"
                  variant="outlined"
                  onClick={(e) =>
                    handleClick(e, "/account/list/registration")
                  }
                  sx={{ width: "208px" }}
                >
                  アカウント発行
                </Button>
              ) : null}
              <Button
                name="siteByUsersDownloadButton"
                variant="outlined"
                onClick={() => {
                  void handleSiteByUsersDownload();
                }}
                sx={{ width: "208px" }}
              >
                SS毎アカウントリスト取得
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "fit-content",
              justifyContent: "center",
              gap: "16px",
              paddingTop: "32px",
              paddingRight: "40px",
              paddingLeft: "40px",
              paddingBottom: "32px",
              "@media screen and (max-width:900px)": {
                padding: "16px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: "min-content",
                paddingLeft: "40px",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  padding: "0",
                },
              }}
            >
              検索結果
            </Box>
          </Box>
          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
            <TableContainer>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                        sx={{
                          padding: "8px",
                          fontWeight: "bold",
                          borderBottom: 2,
                          borderBottomColor: "outline.dark",
                          "@media screen and (max-width:900px)": {
                            display:
                              column.isSmallScreen === true
                                ? "table-cell"
                                : "none",
                          },
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((rowUser, index) => {
                    return (
                      <TableRow
                        {...({
                          name: `searchResult_${index}`,
                        } as ComponentProps<typeof TableRow>)}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={rowUser.id}
                        onClick={() => handleLocationCodeClick(rowUser)}
                        sx={{
                          pointerEvents: "none",
                          "@media screen and (max-width:900px)": {
                            cursor: "pointer",
                            pointerEvents: "auto",
                          },
                        }}
                      >
                        {columns.map((column) => {
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              sx={{
                                padding: "8px",
                                "@media screen and (max-width:900px)": {
                                  display:
                                    column.isSmallScreen === true
                                      ? "table-cell"
                                      : "none",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "32px",
                                }}
                              >
                                {column.button ? (
                                  <ButtonValidate
                                    userId={rowUser.id}
                                    userRole={userRole}
                                    onNavigate={handleClick}
                                  />
                                ) : column.role ? (
                                  <RolesDisplay
                                    organizations={rowUser.m_organizations}
                                  />
                                ) : column.organizations ? (
                                  <OrganizationsDisplay
                                    organizations={rowUser.m_organizations}
                                  />
                                ) : (
                                  getScalarCellValue(rowUser, column)
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
              <Box
                sx={{
                  display: "flex",
                  height: "56px",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px",
                  borderBottom: "2px solid #CCC",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    paddingLeft: "8px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  アカウント詳細
                </Box>
                <IconButton
                  aria-label="close"
                  onClick={handlePopoverClose}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
                <TableBody>
                  {selectedRow &&
                    columns.slice(0, 4).map((column) => {
                      return (
                        <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                          <TableCell
                            align={column.align}
                            sx={{
                              color: "outline.dark",
                              fontWeight: "bold",
                              boxSizing: "border-box",
                              width: "calc((100vw - 64px) / 2)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                WebkitWritingMode: "horizontal-tb",
                                height: "100%",
                              }}
                            >
                              {column.label}
                            </Box>
                          </TableCell>
                          <TableCell
                            align={column.align}
                            sx={{
                              writingMode: "horizontal-tb",
                              boxSizing: "border-box",
                              width: "calc((100vw - 64px) / 2)",
                              overflowWrap: "break-word",
                            }}
                          >
                            {column.button ? (
                              <ButtonValidate
                                userId={selectedRow.id}
                                userRole={userRole}
                                onNavigate={handleClick}
                              />
                            ) : column.role ? (
                              <RolesDisplay
                                organizations={selectedRow.m_organizations}
                              />
                            ) : column.organizations ? (
                              <OrganizationsDisplay
                                organizations={selectedRow.m_organizations}
                              />
                            ) : (
                              getScalarCellValue(selectedRow, column)
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              <Box
                sx={{
                  margin: "16px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {selectedRow ? (
                  <ButtonValidate
                    userId={selectedRow.id}
                    userRole={userRole}
                    onNavigate={handleClick}
                  />
                ) : null}
              </Box>
            </Dialog>
            <SnackbarCustom
              data={snackBarstatus}
              openStatus={snackbarOpen}
              onClose={() => setSnackbarOpen(false)}
            />
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={totalData}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
                  {
                    margin: "0 !important",
                    padding: "0 !important",
                  },
              }}
              labelRowsPerPage="表示件数:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Paper>
        </Box>
      </Box>
    </>
  );
}
